import { motion } from 'framer-motion'
import { Link, useParams, useNavigate } from 'react-router-dom'
import {
  School, Monitor, Cpu, Heart, HardHat, Shield, ArrowRight, ArrowLeft,
  CheckCircle2, Building2, Users, Globe, Phone
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'

const services = [
  {
    id: 'furniture',
    icon: School,
    title: 'Furniture Solutions',
    tagline: 'Premium Institutional Furniture',
    description: 'Complete range of ergonomic and durable furniture solutions designed for educational institutions, hospitals, offices, and government establishments.',
    features: [
      'School & Classroom Furniture',
      'Laboratory Furniture',
      'Hospital & Medical College Furniture',
      'Office & Administrative Furniture',
      'Cafeteria & Canteen Solutions',
      'Library Furniture Systems',
    ],
    benefits: [
      'Ergonomic designs for comfort',
      'Durable materials for longevity',
      'Customizable configurations',
      'Bulk order capabilities',
      'Installation support',
      'Maintenance services',
    ],
    color: 'from-blue-500 to-cyan-500',
    gradient: 'from-blue-600 via-indigo-600 to-purple-700',
  },
  {
    id: 'smart-education',
    icon: Monitor,
    title: 'Smart Education Solutions',
    tagline: 'Transforming Learning Environments',
    description: 'State-of-the-art smart classroom solutions that enhance teaching effectiveness and student engagement through interactive technology.',
    features: [
      'Interactive Displays & Smart Boards',
      'Digital Learning Platforms',
      'Educational Tablets & Devices',
      'E-Learning Infrastructure',
      'Virtual Classroom Solutions',
      'Content Development Services',
    ],
    benefits: [
      'Enhanced student engagement',
      'Modern teaching tools',
      'Interactive learning experience',
      'Remote learning capabilities',
      'Teacher training support',
      'Ongoing technical support',
    ],
    color: 'from-purple-500 to-pink-500',
    gradient: 'from-purple-600 via-violet-600 to-fuchsia-600',
  },
  {
    id: 'digital',
    icon: Cpu,
    title: 'Digital Transformation',
    tagline: 'Technology-Driven Growth',
    description: 'Comprehensive digital solutions including software development, web applications, mobile apps, and AI-powered systems for business transformation.',
    features: [
      'Custom Software Development',
      'Web Application Development',
      'Mobile App Solutions',
      'ERP System Implementation',
      'AI & Machine Learning Solutions',
      'Document Digitization Services',
    ],
    benefits: [
      'Streamlined operations',
      'Improved efficiency',
      'Data-driven decisions',
      'Scalable solutions',
      'Modern user experience',
      'Competitive advantage',
    ],
    color: 'from-green-500 to-emerald-500',
    gradient: 'from-emerald-500 via-teal-600 to-cyan-600',
  },
  {
    id: 'healthcare',
    icon: Heart,
    title: 'Healthcare Solutions',
    tagline: 'Medical Infrastructure Excellence',
    description: 'Complete healthcare infrastructure solutions including medical equipment, nutrition programs, and central kitchen setups for hospitals and institutions.',
    features: [
      'Medical Infrastructure Setup',
      'Hospital Equipment Supply',
      'Nutrition Programs',
      'Dietary Supplements',
      'Central Kitchen Solutions',
      'Healthcare Staff Training',
    ],
    benefits: [
      'Quality healthcare delivery',
      'Compliant infrastructure',
      'Nutritional support',
      'Efficient operations',
      'Staff training programs',
      'Maintenance services',
    ],
    color: 'from-red-500 to-orange-500',
    gradient: 'from-red-500 via-rose-600 to-pink-600',
  },
  {
    id: 'infrastructure',
    icon: HardHat,
    title: 'Infrastructure Solutions',
    tagline: 'Building Better Spaces',
    description: 'Comprehensive infrastructure development services including flooring, playgrounds, gym setups, and complete institutional development.',
    features: [
      'Vinyl & Epoxy Flooring',
      'Playground Development',
      'Gym Infrastructure Setup',
      'Sports Facility Creation',
      'Institutional Renovation',
      'Civil Work Solutions',
    ],
    benefits: [
      'Modern facilities',
      'Safe environments',
      'Durable construction',
      'Professional installation',
      'Quality materials',
      'Long-term warranties',
    ],
    color: 'from-yellow-500 to-amber-500',
    gradient: 'from-amber-500 via-orange-600 to-red-600',
  },
  {
    id: 'security',
    icon: Shield,
    title: 'Security Solutions',
    tagline: 'Comprehensive Protection',
    description: 'Advanced security systems including CCTV, surveillance, and monitoring platforms for complete institutional and enterprise security.',
    features: [
      'CCTV Camera Systems',
      'Video Surveillance Solutions',
      'Access Control Systems',
      'Monitoring Platforms',
      'Security Integration',
      '24/7 Support Services',
    ],
    benefits: [
      'Round-the-clock monitoring',
      'Deter theft and vandalism',
      'Evidence documentation',
      'Remote monitoring',
      'Professional installation',
      'Maintenance support',
    ],
    color: 'from-indigo-500 to-violet-500',
    gradient: 'from-slate-600 via-gray-700 to-zinc-800',
  },
]

const industries = [
  { icon: Building2, name: 'Government Departments', projects: '100+' },
  { icon: School, name: 'Schools & Colleges', projects: '200+' },
  { icon: Heart, name: 'Hospitals & Medical Colleges', projects: '50+' },
  { icon: Users, name: 'Corporate Offices', projects: '150+' },
  { icon: Globe, name: 'Smart City Projects', projects: '20+' },
]

function ServiceDetail({ service }: { service: typeof services[0] }) {
  const IconComponent = service.icon
  const navigate = useNavigate()

  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative py-24 md:py-32">
        <div className="absolute inset-0">
          <div className={`absolute inset-0 bg-gradient-to-br ${service.gradient}`} />
          <div className="absolute inset-0 bg-[url('/images/pattern.svg')] opacity-10" />
        </div>
        <div className="container-premium relative">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl"
          >
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/services')}
              className="mb-6"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Services
            </Button>

            <div className="flex items-center gap-4 mb-6">
              <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${service.color} flex items-center justify-center`}>
                <IconComponent className="w-8 h-8 text-white" />
              </div>
              <div>
                <div className="text-primary-400 text-sm font-medium">{service.tagline}</div>
                <h1 className="heading-lg text-white">{service.title}</h1>
              </div>
            </div>

            <p className="text-xl text-graphite-300 mb-8">
              {service.description}
            </p>

            <div className="flex flex-wrap gap-4">
              <Button variant="gold" asChild>
                <Link to="/contact">
                  Request Quote
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </Button>
              <Button variant="outline" asChild>
                <Link to="/projects">View Related Projects</Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features & Benefits */}
      <section className="section-padding">
        <div className="container-premium">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Features */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <Card variant="premium" className="p-8 h-full">
                <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${service.color} flex items-center justify-center`}>
                    <CheckCircle2 className="w-5 h-5 text-white" />
                  </div>
                  What We Offer
                </h2>
                <div className="space-y-4">
                  {service.features.map((feature, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                      className="flex items-start gap-3 p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
                    >
                      <div className={`w-6 h-6 rounded-full bg-gradient-to-br ${service.color} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                        <CheckCircle2 className="w-3.5 h-3.5 text-white" />
                      </div>
                      <span className="text-graphite-200">{feature}</span>
                    </motion.div>
                  ))}
                </div>
              </Card>
            </motion.div>

            {/* Benefits */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <Card variant="premium" className="p-8 h-full">
                <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${service.color} flex items-center justify-center`}>
                    <ArrowRight className="w-5 h-5 text-white" />
                  </div>
                  Key Benefits
                </h2>
                <div className="space-y-4">
                  {service.benefits.map((benefit, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: 20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                      className="flex items-start gap-3 p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
                    >
                      <div className={`w-6 h-6 rounded-full bg-gradient-to-br ${service.color} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                        <ArrowRight className="w-3.5 h-3.5 text-white" />
                      </div>
                      <span className="text-graphite-200">{benefit}</span>
                    </motion.div>
                  ))}
                </div>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Industries */}
      <section className="section-padding bg-gradient-to-b from-graphite-950 via-primary-950/10 to-graphite-950">
        <div className="container-premium">
          <div className="text-center mb-12">
            <h2 className="heading-md text-white mb-4">Industries We Serve</h2>
            <p className="text-graphite-400">Trusted across diverse sectors with specialized solutions</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
            {industries.map((industry, index) => (
              <motion.div
                key={industry.name}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Link to="/industries">
                  <Card variant="glass" className="p-6 text-center hover:border-primary-500/50 transition-all group cursor-pointer">
                    <industry.icon className="w-10 h-10 mx-auto mb-3 text-primary-400 group-hover:text-primary-300 transition-colors" />
                    <h3 className="text-white font-semibold mb-1">{industry.name}</h3>
                    <p className="text-sm text-graphite-500">{industry.projects} Projects</p>
                  </Card>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding">
        <div className="container-premium">
          <Card variant="premium" className="p-12 text-center relative overflow-hidden">
            <div className={`absolute inset-0 bg-gradient-to-br ${service.gradient} opacity-20`} />
            <div className="relative">
              <h2 className="heading-md text-white mb-4">Ready to Get Started?</h2>
              <p className="text-graphite-400 mb-8 max-w-2xl mx-auto">
                Contact us today for a free consultation and quote. Our team will work with you
                to understand your requirements and provide tailored solutions.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button variant="gold" size="lg" asChild>
                  <Link to="/contact">
                    Request Proposal
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Link>
                </Button>
                <Button variant="outline" size="lg" asChild>
                  <Link to="/contact">
                    <Phone className="mr-2 w-5 h-5" />
                    Call Us
                  </Link>
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </section>
    </div>
  )
}

function ServicesList() {
  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative py-24 md:py-32">
        <div className="absolute inset-0 bg-gradient-to-b from-primary-900/50 to-transparent" />
        <div className="container-premium relative">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl mx-auto text-center"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 mb-6">
              <Cpu className="w-4 h-4 text-primary-400" />
              <span className="text-sm text-graphite-300">Our Services</span>
            </div>
            <h1 className="heading-xl text-white mb-6">
              Comprehensive <span className="gradient-text">Solutions</span>
              <br />
              for Every Need
            </h1>
            <p className="text-xl text-graphite-300">
              From smart classrooms to healthcare infrastructure, we deliver end-to-end
              solutions tailored to government, education, and corporate requirements.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="section-padding">
        <div className="container-premium">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => {
              const IconComponent = service.icon
              return (
                <motion.div
                  key={service.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link to={`/services/${service.id}`}>
                    <Card variant="hover" className="h-full overflow-hidden group cursor-pointer">
                      {/* Thumbnail */}
                      <div className="relative aspect-[3/2] overflow-hidden">
                        <div className={`absolute inset-0 bg-gradient-to-br ${service.gradient}`} />
                        <div className="absolute inset-0 bg-[url('/images/pattern.svg')] opacity-20" />
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="w-20 h-20 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                            <IconComponent className="w-10 h-10 text-white" />
                          </div>
                        </div>
                        <div className="absolute bottom-4 left-4">
                          <span className={`px-3 py-1.5 rounded-full bg-gradient-to-r ${service.color} text-white text-xs font-medium`}>
                            {service.tagline}
                          </span>
                        </div>
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                          <span className="text-white font-medium flex items-center gap-2">
                            View Details
                            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                          </span>
                        </div>
                      </div>

                      {/* Content */}
                      <div className="p-6">
                        <h3 className="text-xl font-semibold text-white mb-3 group-hover:text-primary-400 transition-colors">
                          {service.title}
                        </h3>
                        <p className="text-graphite-400 mb-4 line-clamp-2">
                          {service.description}
                        </p>

                        {/* Features Preview */}
                        <div className="space-y-2 mb-4">
                          {service.features.slice(0, 3).map((feature, i) => (
                            <div key={i} className="flex items-center gap-2 text-sm text-graphite-300">
                              <CheckCircle2 className="w-4 h-4 text-primary-400 flex-shrink-0" />
                              <span>{feature}</span>
                            </div>
                          ))}
                        </div>

                        <div className="flex items-center justify-between pt-4 border-t border-white/10">
                          <span className="text-primary-400 font-medium group-hover:text-primary-300 transition-colors">
                            Learn More
                          </span>
                          <ArrowRight className="w-5 h-5 text-primary-400 group-hover:translate-x-1 transition-all" />
                        </div>
                      </div>
                    </Card>
                  </Link>
                </motion.div>
              )
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding">
        <div className="container-premium">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="relative overflow-hidden rounded-3xl"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-primary-600 to-secondary-600" />
            <div className="absolute inset-0 bg-[url('/images/pattern.svg')] opacity-10" />

            <div className="relative px-8 py-16 md:px-16 md:py-24 text-center">
              <h2 className="heading-lg text-white mb-6">
                Need a Custom Solution?
              </h2>
              <p className="text-xl text-white/80 mb-8 max-w-2xl mx-auto">
                Our team can design bespoke solutions tailored to your specific requirements.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button variant="gold" size="xl" asChild>
                  <Link to="/contact">
                    Contact Us Today
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Link>
                </Button>
                <Button variant="ghost" size="xl" asChild className="bg-white/10 border-white/20">
                  <Link to="/franchise">Become a Partner</Link>
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  )
}

export default function Services() {
  const { serviceId } = useParams<{ serviceId: string }>()

  // If specific service page
  if (serviceId) {
    const service = services.find(s => s.id === serviceId)
    if (service) {
      return <ServiceDetail service={service} />
    }
  }

  // Default: show services list
  return <ServicesList />
}